import { useEffect, useState, useRef } from "react";
import api, { apiError, fcfa } from "../lib/api";
import { toast } from "sonner";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { Receipt, Paperclip, X } from "lucide-react";

const STATUS_LABEL = { submitted: "En attente", processing: "En cours", validated: "Validée", reimbursed: "Remboursée", rejected: "Refusée" };
const STATUS_CLS = {
  validated: "bg-[#16a34a]/15 text-[#16794a]", reimbursed: "bg-[#16a34a]/15 text-[#16794a]",
  rejected: "bg-[#D32F2F]/15 text-[#D32F2F]", processing: "bg-[#2563EB]/15 text-[#2563EB]",
  submitted: "bg-[#F26419]/25 text-[#B8860B]",
};

export function ExpensesCard() {
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [type, setType] = useState("expense");
  const [amount, setAmount] = useState("");
  const [category, setCategory] = useState("Transport");
  const [note, setNote] = useState("");
  const [attachment, setAttachment] = useState(null);
  const [saving, setSaving] = useState(false);
  const [reply, setReply] = useState({});
  const fileRef = useRef(null);

  const load = () => api.get("/agent/expenses").then((r) => { setItems(r.data.items || []); setTotal(r.data.total || 0); }).catch(() => {});
  useEffect(() => { load(); }, []);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    if (f.size > 2_500_000) return toast.error("Photo trop lourde (max 2,5 Mo)");
    const reader = new FileReader();
    reader.onload = () => setAttachment(reader.result);
    reader.readAsDataURL(f);
  };

  const submit = async () => {
    if (type === "expense" && (!amount || Number(amount) <= 0)) return toast.error("Montant invalide");
    if (type === "note" && !note.trim()) return toast.error("Écrivez votre note");
    setSaving(true);
    try {
      await api.post("/agent/expenses", { type, amount: type === "expense" ? Number(amount) : 0, category, note, attachment });
      toast.success(type === "note" ? "Note envoyée à l'administration" : "Note de frais envoyée à l'administration");
      setAmount(""); setNote(""); setAttachment(null); if (fileRef.current) fileRef.current.value = ""; load();
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  const cats = ["Transport", "Carburant", "Communication", "Repas", "Autre"];

  const sendReply = async (id) => {
    const text = (reply[id] || "").trim();
    if (!text) return;
    try { await api.post(`/expenses/${id}/reply`, { text }); setReply((s) => ({ ...s, [id]: "" })); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  return (
    <div data-testid="agent-expenses-card" className="bg-white rounded-2xl border border-[#E2DFD8] p-6 lg:col-span-2">
      <h2 className="font-head font-bold text-lg text-[#1A1A1A] flex items-center gap-2"><Receipt className="w-5 h-5 text-[#D94F2B]" /> Notes & notes de frais</h2>
      <p className="text-xs text-[#75807A] mt-1">Tout ce que vous envoyez ici arrive directement à l'administration pour vérification.</p>

      <div className="mt-4 flex gap-2">
        <button data-testid="expense-type-expense" onClick={() => setType("expense")} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${type === "expense" ? "bg-[#F26419] text-white" : "bg-[#F0EBE1] text-[#75807A]"}`}>Note de frais</button>
        <button data-testid="expense-type-note" onClick={() => setType("note")} className={`h-9 px-4 rounded-full text-sm font-semibold transition-colors ${type === "note" ? "bg-[#F26419] text-white" : "bg-[#F0EBE1] text-[#75807A]"}`}>Note simple</button>
      </div>

      <div className="mt-4 grid sm:grid-cols-4 gap-3">
        {type === "expense" && (<>
          <Input data-testid="expense-amount" type="number" value={amount} onChange={(e) => setAmount(e.target.value)} placeholder="Montant (FCFA)" className="h-12 rounded-xl border-2" />
          <select data-testid="expense-category" value={category} onChange={(e) => setCategory(e.target.value)} className="h-12 rounded-xl border-2 px-3 text-sm bg-white">
            {cats.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </>)}
        <Input data-testid="expense-note" value={note} onChange={(e) => setNote(e.target.value)} placeholder={type === "note" ? "Votre message / compte-rendu" : "Description"} className={`h-12 rounded-xl border-2 ${type === "note" ? "sm:col-span-4" : "sm:col-span-2"}`} />
      </div>

      <div className="mt-3 flex items-center gap-3 flex-wrap">
        <input ref={fileRef} data-testid="expense-file" type="file" accept="image/*" onChange={onFile} className="hidden" />
        <Button data-testid="expense-attach" type="button" onClick={() => fileRef.current?.click()} className="h-10 px-4 rounded-xl bg-white border-2 border-[#E2DFD8] text-[#1A1A1A] text-sm font-semibold hover:border-[#D94F2B]"><Paperclip className="w-4 h-4 mr-1" />{attachment ? "Photo jointe" : "Joindre une photo"}</Button>
        {attachment && (
          <div className="relative">
            <img src={attachment} alt="justificatif" className="w-14 h-14 rounded-lg object-cover border border-[#E2DFD8]" />
            <button data-testid="expense-att-remove" onClick={() => { setAttachment(null); if (fileRef.current) fileRef.current.value = ""; }} className="absolute -top-2 -right-2 w-5 h-5 rounded-full bg-[#D32F2F] text-white flex items-center justify-center"><X className="w-3 h-3" /></button>
          </div>
        )}
        <Button data-testid="expense-submit" onClick={submit} disabled={saving} className="h-11 px-5 rounded-xl bg-[#F26419] text-white font-bold hover:bg-[#D85312] ml-auto">{saving ? "..." : "Envoyer à l'admin"}</Button>
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between text-sm font-semibold text-[#1A1A1A]"><span>Historique</span><span data-testid="expense-total">Total frais : {fcfa(total)}</span></div>
        <div className="mt-2 divide-y divide-[#F0EBE1]">
          {items.length === 0 && <p className="text-sm text-[#75807A] py-3" data-testid="expense-empty">Aucune note pour le moment.</p>}
          {items.map((x) => (
            <div key={x.id} data-testid={`expense-row-${x.id}`} className="py-2.5">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-3 min-w-0">
                  {x.attachment && <img src={x.attachment} alt="" className="w-10 h-10 rounded-lg object-cover border border-[#E2DFD8] shrink-0" />}
                  <div className="min-w-0">
                    <div className="text-sm font-semibold text-[#1A1A1A]">{x.type === "note" ? "Note" : `${fcfa(x.amount)} · ${x.category}`}</div>
                    <div className="text-xs text-[#75807A] truncate">{x.note || "—"} · {new Date(x.created_at).toLocaleDateString("fr-FR")}</div>
                  </div>
                </div>
                <span className={`text-[11px] px-2 py-1 rounded-full font-semibold shrink-0 ${STATUS_CLS[x.status] || "bg-[#F0EBE1] text-[#75807A]"}`}>{STATUS_LABEL[x.status] || x.status}</span>
              </div>
              {(x.replies || []).length > 0 && (
                <div className="mt-2 pl-2 space-y-1.5" data-testid={`expense-replies-${x.id}`}>
                  {(x.replies || []).map((rp) => (
                    <div key={rp.id} className={`text-xs rounded-lg px-2.5 py-1.5 ${rp.author_role === "admin" ? "bg-[#F26419]/10" : "bg-[#F0EBE1]"}`}>
                      <span className="font-bold text-[#75807A]">{rp.author_role === "admin" ? "Administration" : "Vous"} :</span> {rp.text}
                    </div>
                  ))}
                </div>
              )}
              <div className="mt-1.5 flex gap-2">
                <Input data-testid={`expense-reply-${x.id}`} value={reply[x.id] || ""} onChange={(e) => setReply((s) => ({ ...s, [x.id]: e.target.value }))} onKeyDown={(e) => e.key === "Enter" && sendReply(x.id)} placeholder="Répondre / commenter…" className="h-9 rounded-lg border-2 flex-1 text-sm" />
                <Button data-testid={`expense-reply-send-${x.id}`} onClick={() => sendReply(x.id)} className="h-9 px-3 rounded-lg bg-[#1A1A1A] text-white text-xs font-bold">Répondre</Button>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
